import { useMemo } from 'react';
import { useMyStore } from '@/queries/useStoreData';
import { PRODUCT_TYPES } from '@/lib/constants';
import type { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';

export interface GrantableProductOption {
    name: string;
    page_id: number;
    image?: string;
}

// Product types that can have access granted
const GRANTABLE_PRODUCT_TYPES: string[] = [
    PRODUCT_TYPES.COURSE,
    PRODUCT_TYPES.COMMUNITY,
    PRODUCT_TYPES.DIGITAL_DOWNLOAD,
];

/**
 * Hook to get products that can have access granted
 * Returns courses, communities, and digital downloads
 */
export function useGrantableProducts() {
    const { data: stores, isLoading } = useMyStore();

    const availableProducts = useMemo((): GrantableProductOption[] => {
        if (!stores || stores.length === 0) return [];

        const products: GrantableProductOption[] = [];

        stores.forEach(store => {
            store.pages?.forEach(page => {
                const pageData = page.data;
                const productType = pageData?.product?.type;
                if (productType && GRANTABLE_PRODUCT_TYPES.includes(productType)) {
                    products.push({
                        name: pageData?.product?.title || 'Unnamed Product',
                        page_id: page.page_id,
                        image: pageData?.location === 'store' ? pageData?.button?.image : pageData?.product?.image,
                    });
                }
            });
        });

        return products;
    }, [stores]);

    const dropdownOptions = useMemo((): DropdownMenuOption[] => {
        return availableProducts.map(product => ({
            label: product.name,
            value: String(product.page_id),
            icon: product.image,
        }));
    }, [availableProducts]);

    const findProductByPageId = (pageId: string): GrantableProductOption | undefined => {
        return availableProducts.find(p => String(p.page_id) === pageId);
    };

    return {
        availableProducts,
        dropdownOptions,
        findProductByPageId,
        isLoading,
    };
}
